import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Coins, Award, Zap, Gift, Rocket, Lock } from "lucide-react";

const CoinsInfoSection = () => {
  const earnCoins = [
    {
      icon: <Award size={30} color="#c59fff" />,
      title: "Solve Code Issues",
      desc: "Earn coins every time you solve issues posted by developers.",
    },
    {
      icon: <Zap size={30} color="#c59fff" />,
      title: "Post Helpful Suggestions",
      desc: "Give quality suggestions or optimizations and earn bonus coins.",
    },
    {
      icon: <Gift size={30} color="#c59fff" />,
      title: "Daily Streak Rewards",
      desc: "Log in daily and stay active to unlock daily streak coins.",
      soon: true,
    },
    {
      icon: <Rocket size={30} color="#c59fff" />,
      title: "Referral Bonus",
      desc: "Invite developers and earn coins when they solve their first issue.",
      soon: true,
    },
  ];

  const useCoins = [
    {
      icon: <Coins size={28} color="#c59fff" />,
      title: "Promote Your Profile",
      desc: "Boost your developer profile to appear in top searches.",
      soon: true,
    },
    {
      icon: <Award size={28} color="#c59fff" />,
      title: "Unlock Premium Badges",
      desc: "Get special badges that increase your credibility in the community.",
      soon: true,
    },
    {
      icon: <Gift size={28} color="#c59fff" />,
      title: "Redeem Rewards",
      desc: "Use coins to redeem real rewards, coupons, and perks.",
      soon: true,
    },
  ];

  return (
    <SectionWrapper>
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Highlight>Coins System</Highlight> on SolveMyCodeIssue
      </motion.h2>

      <Subtitle>
        Learn how you can earn and use coins to level up your developer journey
        🚀
      </Subtitle>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <SectionTitle>💰 How to Earn Coins</SectionTitle>
        <CardGrid>
          {earnCoins.map((item, i) => (
            <Card key={i} soon={item.soon}>
              {item.soon && <SoonBadge>Coming Soon</SoonBadge>}
              <IconBox>{item.icon}</IconBox>
              <h4>{item.title}</h4>
              <p>{item.desc}</p>
            </Card>
          ))}
        </CardGrid>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <SectionTitle>🎯 What You Can Do With Coins</SectionTitle>
        <CardGrid>
          {useCoins.map((item, i) => (
            <Card key={i} soon={item.soon}>
              {item.soon && <SoonBadge>Coming Soon</SoonBadge>}
              <IconBox>{item.icon}</IconBox>
              <h4>{item.title}</h4>
              <p>{item.desc}</p>
            </Card>
          ))}
        </CardGrid>
      </motion.div>
    </SectionWrapper>
  );
};

export default CoinsInfoSection;

/* ---------------- Styled Components ---------------- */

const SectionWrapper = styled.section`
  width: 100%;
  background: linear-gradient(180deg, #0b0617, #000);
  color: #fff;
  padding: 100px 20px;
  text-align: center;
`;

const Highlight = styled.span`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #ccc;
  margin-top: 10px;
  margin-bottom: 50px;
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  color: #c59fff;
  margin-bottom: 30px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin-bottom: 70px;
`;

const Card = styled.div`
  position: relative;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 16px;
  padding: 30px 20px;
  text-align: center;
  backdrop-filter: blur(8px);
  transition: all 0.3s;
  overflow: hidden;

  &:hover {
    transform: translateY(-5px);
    border-color: #c59fff;
    box-shadow: 0 0 20px rgba(155, 92, 255, 0.3);
  }

  h4 {
    font-size: 1.1rem;
    color: #fff;
    margin-top: 10px;
    margin-bottom: 10px;
  }

  p {
    color: #ccc;
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

const IconBox = styled.div`
  width: 60px;
  height: 60px;
  margin: 0 auto;
  border-radius: 50%;
  background: rgba(155, 92, 255, 0.15);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SoonBadge = styled.span`
  position: absolute;
  top: 12px;
  right: -28px;
  background: #ff7b00;
  color: #fff;
  font-size: 0.75rem;
  padding: 4px 40px;
  transform: rotate(35deg);
  font-weight: bold;
  box-shadow: 0 0 10px rgba(255, 123, 0, 0.5);
`;
